// ═══════════════════════════════════════════════════════════════════════════════
// Employer budget list — Screen 2.4 (tabs: active / settled, cards with spend & remaining)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { AppPageTitleSection } from '../../components/ui/AppPageTitleSection';
import { PagePrimaryFab } from '../../components/ui/PagePrimaryFab';
import { NativeDateInput } from '../../components/NativeDateInput';
import { budgetApi, settlementApi } from '../../lib/api';
import type { BudgetListItem } from '@fdh/shared';

export const BudgetListPage = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState<BudgetListItem[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterError, setFilterError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blockingSettlements, setBlockingSettlements] = useState<
    { startDate: string; endDate: string; status: string }[]
  >([]);

  const formatMoney = (n: number) => (n < 0 ? `-$${Math.abs(n).toFixed(2)}` : `$${n.toFixed(2)}`);

  const formatYmd = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;

  const defaultPast30Days = (): { start: string; end: string } => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 29);
    return { start: formatYmd(start), end: formatYmd(end) };
  };

  const load = useCallback(async () => {
    if (!startDate || !endDate) {
      setFilterError(t('budget.validation.rangeRequired'));
      setItems([]);
      setLoading(false);
      return;
    }
    if (startDate > endDate) {
      setFilterError(t('budget.validation.dates'));
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setFilterError(null);
    try {
      const res = await budgetApi.list('active', 1, 100, startDate, endDate);
      if (res.success && res.data) {
        const list = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        setItems(list);
        return;
      }
      setError(res.error ?? t('budget.loadError'));
    } catch {
      setError(t('budget.loadError'));
    } finally {
      setLoading(false);
    }
  }, [endDate, startDate, t]);

  useEffect(() => {
    const { start, end } = defaultPast30Days();
    setStartDate(start);
    setEndDate(end);
  }, []);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }
    void load();
  }, [endDate, load, startDate]);

  const handleToggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };
  const shouldHideEditButton = (budgetStartDate: string, budgetEndDate: string): boolean =>
    blockingSettlements.some(
      (row) =>
        (row.status === 'submitted' || row.status === 'reviewed') &&
        row.startDate <= budgetEndDate &&
        row.endDate >= budgetStartDate
    );

  useEffect(() => {
    const loadBlockingSettlements = async () => {
      const pageSize = 100;
      const maxPages = 20;
      let page = 1;
      const merged: { startDate: string; endDate: string; status: string }[] = [];
      while (page <= maxPages) {
        const res = await settlementApi.list('all', page, pageSize);
        if (!res.success || !res.data) {
          break;
        }
        const rows = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        merged.push(
          ...rows.map((row) => ({
            startDate: row.startDate,
            endDate: row.endDate,
            status: row.status,
          }))
        );
        if (rows.length < pageSize) {
          break;
        }
        page += 1;
      }
      setBlockingSettlements(merged);
    };
    void loadBlockingSettlements();
  }, []);

  return (
    <div className="min-h-full bg-gray-50 px-4 py-6 pb-24 dark:bg-gray-950">
      <div className="mx-auto max-w-lg">
        <AppPageTitleSection title={t('budget.title')} subtitle={t('budget.subtitle')}>
          <div className="flex flex-col items-end gap-1">
            <Link
              to="/dashboard"
              className="text-sm font-medium text-primary-600 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 dark:text-primary-400"
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/settlements"
              className="text-sm font-medium text-primary-600 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 dark:text-primary-400"
            >
              {t('settlement.navLink')}
            </Link>
          </div>
        </AppPageTitleSection>

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('budget.filterStartDate')}
            <NativeDateInput
              value={startDate}
              onChange={(event) => {
                setStartDate(event.target.value);
                setExpandedGroups({});
              }}
              max={endDate || undefined}
              wrapperClassName="mt-1 block w-full"
              className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            />
          </label>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('budget.filterEndDate')}
            <NativeDateInput
              value={endDate}
              onChange={(event) => {
                setEndDate(event.target.value);
                setExpandedGroups({});
              }}
              min={startDate || undefined}
              max={formatYmd(new Date())}
              wrapperClassName="mt-1 block w-full"
              className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            />
          </label>
        </div>
        {filterError ? (
          <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {filterError}
          </p>
        ) : null}

        <div id="budget-panel" role="region" aria-label={t('budget.title')}>
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          ) : error ? (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6 text-center dark:border-gray-600 dark:bg-gray-900">
              <p className="text-sm text-gray-700 dark:text-gray-200">
                {t('budget.empty')}
              </p>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('budget.emptyHint')}</p>
            </div>
          ) : (
            <ul className="space-y-3" role="list">
              {items.map((row) => {
                const hideEdit = shouldHideEditButton(row.startDate, row.endDate);
                return (
                <li key={row.id}>
                  <article className="rounded-xl bg-white p-4 shadow-sm transition dark:bg-gray-900 dark:shadow-black/20">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {t('settlement.period', { start: row.startDate, end: row.endDate })}
                        </p>
                        {row.isMergedGroup && row.mergedChildCount ? (
                          <p className="mt-1 text-xs font-medium text-orange-900 dark:text-orange-300">
                            {t('budget.mergedChildCount', { count: row.mergedChildCount })}
                          </p>
                        ) : null}
                        {row.isLocked ? (
                          <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-950/50 dark:text-amber-100">
                            {t('budget.badgeLocked')}
                          </span>
                        ) : null}
                      </div>
                      {row.isMergedGroup ? (
                        <div className="flex items-center gap-2">
                          {!row.parentBudgetId && !row.isLocked ? (
                            <Link
                              to={`/budgets/new?parentBudgetId=${encodeURIComponent(row.id)}`}
                              className="inline-flex min-h-11 items-center rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200 dark:hover:bg-emerald-900/40"
                              aria-label={t('budget.addOnAria')}
                            >
                              {t('budget.addOn')}
                            </Link>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => handleToggleGroup(row.id)}
                            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-gray-200 px-3 py-1 text-lg font-semibold text-primary-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:text-primary-400 dark:hover:bg-gray-800"
                            aria-label={
                              expandedGroups[row.id]
                                ? t('budget.collapseChildrenAria', {
                                    start: row.startDate,
                                    end: row.endDate,
                                  })
                                : t('budget.expandChildrenAria', {
                                    start: row.startDate,
                                    end: row.endDate,
                                  })
                            }
                            aria-expanded={expandedGroups[row.id] === true}
                          >
                            {expandedGroups[row.id] ? (
                              <span aria-hidden className="text-lg leading-none">
                                −
                              </span>
                            ) : (
                              <svg
                                className="h-5 w-5 shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            )}
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {!row.parentBudgetId && !row.isLocked ? (
                            <Link
                              to={`/budgets/new?parentBudgetId=${encodeURIComponent(row.id)}`}
                              className="inline-flex min-h-11 items-center rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200 dark:hover:bg-emerald-900/40"
                              aria-label={t('budget.addOnAria')}
                            >
                              {t('budget.addOn')}
                            </Link>
                          ) : null}
                          {!hideEdit ? (
                            <Link
                              to={`/budgets/${row.id}/edit`}
                              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-primary-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-700 dark:text-primary-400 dark:hover:bg-gray-800"
                              aria-label={t('budget.editAria', { start: row.startDate, end: row.endDate })}
                            >
                              {t('budget.edit')}
                            </Link>
                          ) : null}
                        </div>
                      )}
                    </div>
                    <dl className="mt-3 space-y-2 text-lg">
                      <div className="flex justify-between">
                        <dt className="font-bold text-gray-600 dark:text-gray-400">{t('budget.totalAmount')}</dt>
                        <dd className="font-bold text-gray-900 dark:text-gray-100">
                          {formatMoney(row.amount)}
                        </dd>
                      </div>
                    </dl>
                    {row.isMergedGroup && row.children && expandedGroups[row.id] ? (
                      <ul className="mt-4 space-y-2 border-t border-gray-200 pt-3 dark:border-gray-700" role="list">
                        {(() => {
                          const subChildren = row.children.filter((child) => Boolean(child.parentBudgetId));
                          const subTotal = subChildren.reduce((sum, child) => sum + child.amount, 0);
                          const mainOnlyAmount = Math.max(0, row.amount - subTotal);
                          return (
                            <>
                        <li key={`${row.id}-main`}>
                          {!hideEdit ? (
                            <Link
                              to={`/budgets/${row.id}/edit`}
                              className="block rounded-lg border border-orange-200 bg-orange-50/80 px-3 py-2 hover:bg-orange-100/80 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-orange-900/50 dark:bg-orange-950/30 dark:hover:bg-orange-900/30"
                              tabIndex={0}
                              aria-label={t('budget.editAria', {
                                start: row.startDate,
                                end: row.endDate,
                              })}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {t('settlement.period', { start: row.startDate, end: row.endDate })}
                                </p>
                                <span className="shrink-0 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-primary-700 dark:border-gray-700 dark:text-primary-400">
                                  {t('budget.edit')}
                                </span>
                              </div>
                              <div className="mt-1 flex items-center justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">{t('budget.mainAmountLabel')}</span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {formatMoney(mainOnlyAmount)}
                                </span>
                              </div>
                            </Link>
                          ) : (
                            <div className="block rounded-lg border border-orange-200 bg-orange-50/80 px-3 py-2 dark:border-orange-900/50 dark:bg-orange-950/30">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {t('settlement.period', { start: row.startDate, end: row.endDate })}
                                </p>
                              </div>
                              <div className="mt-1 flex items-center justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">{t('budget.mainAmountLabel')}</span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {formatMoney(mainOnlyAmount)}
                                </span>
                              </div>
                            </div>
                          )}
                        </li>
                        {subChildren.map((child) => {
                          const hideChildEdit = shouldHideEditButton(child.startDate, child.endDate);
                          return (
                            <li key={child.id}>
                              {!hideChildEdit ? (
                                <Link
                                  to={`/budgets/${child.id}/edit`}
                                  className="block rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 hover:bg-emerald-100/80 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-emerald-900/50 dark:bg-emerald-950/35 dark:hover:bg-emerald-900/35"
                                  tabIndex={0}
                                  aria-label={t('budget.editAria', {
                                    start: child.startDate,
                                    end: child.endDate,
                                  })}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {t('settlement.period', { start: child.startDate, end: child.endDate })}
                                    </p>
                                    <span className="shrink-0 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-primary-700 dark:border-gray-700 dark:text-primary-400">
                                      {t('budget.edit')}
                                    </span>
                                  </div>
                                  <div className="mt-1 flex items-center justify-between text-sm">
                                    <span className="text-gray-600 dark:text-gray-400">{t('budget.subAmountLabel')}</span>
                                    <span className="font-medium text-gray-900 dark:text-gray-100">
                                      {formatMoney(child.amount)}
                                    </span>
                                  </div>
                                </Link>
                              ) : (
                                <div className="block rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 dark:border-emerald-900/50 dark:bg-emerald-950/35">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {t('settlement.period', { start: child.startDate, end: child.endDate })}
                                    </p>
                                  </div>
                                  <div className="mt-1 flex items-center justify-between text-sm">
                                    <span className="text-gray-600 dark:text-gray-400">{t('budget.subAmountLabel')}</span>
                                    <span className="font-medium text-gray-900 dark:text-gray-100">
                                      {formatMoney(child.amount)}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </li>
                          );
                        })}
                            </>
                          );
                        })()}
                      </ul>
                    ) : null}
                  </article>
                </li>
              );
              })}
            </ul>
          )}
        </div>
      </div>

      <PagePrimaryFab to="/budgets/new" ariaLabel={t('budget.addNewAria')}>
        <span className="text-lg font-bold leading-none" aria-hidden>
          +
        </span>
        <span>{t('budget.addNew')}</span>
      </PagePrimaryFab>
    </div>
  );
};
